import store from 'store2';
import status from './status';
import normalize from './config/normalize';
import { createModelInfo } from '../common/ModelInfoContext';

const buildUrl = (url, query) => {
  if (!query) {
    return url;
  }
  const searchParams = new URLSearchParams(query);
  searchParams.sort();
  return `${url}?${searchParams.toString()}`;
};

const buildAuth = (enabled, redirectUrl) => {
  return {
    enabled,
    redirectUrl,
    token: store.get('byo.auth') || ''
  };
};

const checkAuth = auth => res => {
  if (!auth.enabled) {
    return res;
  }
  if (res.status === 401) {
    store.remove('byo.auth');
    if (!TEST) {
      // avoid calling during tests since we can't mock out
      window.location.replace(auth.redirectUrl);
    }
  } else if (res.headers.get('x-byo-auth')) {
    store.set('byo.auth', res.headers.get('x-byo-auth'));
  }
  return res;
};

const fetchGet = (url, auth) => {
  const authHeaders = auth.enabled ? { 'x-byo-auth': auth.token } : {};
  const options = {
    headers: {
      ...authHeaders
    }
  };
  return fetch(url, options)
    .then(checkAuth(auth))
    .then(status)
    .then(res => res.json());
};

const fetchPost = (url, data, auth) => {
  const authHeaders = auth.enabled ? { 'x-byo-auth': auth.token } : {};
  const options = {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json; charset=utf-8',
      ...authHeaders
    },
    body: JSON.stringify(data)
  };
  return fetch(url, options)
    .then(checkAuth(auth))
    .then(status)
    .then(res => res.json());
};

export const fetchConfig = (options = {}) => {
  let cache = window.__cache__;
  const modelInfo = createModelInfo();
  const {
    country,
    brand,
    language,
    region,
    bodyStyle,
    carline,
    year,
    legacyServicesPath,
    enableAuth,
    authRedirectUrl
  } = modelInfo;
  const { postalCode } = options;

  const url = `${legacyServicesPath}/fullyConfigured/${country}/${language}/${brand}/${year}/${carline}/${bodyStyle}`;
  const query = {
    region,
    ...options
  };

  return fetchGet(buildUrl(url, query), buildAuth(enableAuth, authRedirectUrl))
    .then(normalize(modelInfo))
    .then(config => {
      const key = `${config.styleId}-${config.ss}-${postalCode}`;
      cache.set(key, config);
      return config;
    });
};

export const fetchByStyleIdAndSerializedState = (styleId, ss, options = {}) => {
  let cache = window.__cache__;
  const modelInfo = createModelInfo();
  const {
    country,
    brand,
    language,
    region,
    bodyStyle,
    carline,
    year,
    legacyServicesPath,
    enableAuth,
    authRedirectUrl
  } = modelInfo;
  const { postalCode } = options;
  const key = `${styleId}-${ss}-${postalCode}`;
  if (cache.has(key)) {
    return Promise.resolve(cache.get(key));
  }

  const url = `${legacyServicesPath}/retrieveConfiguration/${brand}/${country}/${language}/${region}/${styleId}`;
  const query = {
    carline: options.carline || carline,
    modelyear: options.year || year,
    bodystyle: options.bodyStyle || bodyStyle,
    postalCode: options.postalCode
  };

  return fetchPost(
    buildUrl(url, query),
    { ss },
    buildAuth(enableAuth, authRedirectUrl)
  )
    .then(normalize(modelInfo))
    .then(config => {
      const key = `${config.styleId}-${config.ss}-${postalCode}`;
      cache.set(key, config);
      return config;
    });
};

export const fetchByStyleId = (styleId, options = {}) => {
  let cache = window.__cache__;
  const modelInfo = createModelInfo();
  const {
    country,
    brand,
    language,
    region,
    legacyServicesPath,
    enableAuth,
    authRedirectUrl
  } = modelInfo;
  const { postalCode, rpos } = options;

  const url = `${legacyServicesPath}/fullyConfigured/${country}/${language}/${brand}`;
  let query = {
    styleId,
    postalCode,
    region
  };
  if (rpos) {
    query.rpos = rpos;
  }

  return fetchGet(buildUrl(url, query), buildAuth(enableAuth, authRedirectUrl))
    .then(normalize(modelInfo))
    .then(config => {
      const key = `${config.styleId}-${config.ss}-${postalCode}`;
      cache.set(key, config);
      return config;
    });
};

export const fetchToggleOption = (styleId, ss, id, options = {}) => {
  let cache = window.__cache__;
  const modelInfo = createModelInfo();
  const {
    country,
    brand,
    language,
    region,
    carline,
    year,
    bodyStyle,
    legacyServicesPath,
    enableAuth,
    authRedirectUrl
  } = modelInfo;
  const { postalCode } = options;

  const url = `${legacyServicesPath}/toggleOptionData/${brand}/${country}/${language}/${region}/${styleId}`;
  const query = {
    carline,
    modelyear: year,
    bodystyle: bodyStyle,
    postalCode
  };

  return fetchPost(
    buildUrl(url, query),
    { rpo: id, ss },
    buildAuth(enableAuth, authRedirectUrl)
  )
    .then(normalize(modelInfo))
    .then(config => {
      const key = `${config.styleId}-${config.ss}-${postalCode}`;
      cache.set(key, config);
      return config;
    });
};
